/**
 * useArtTransactions — Real-time transaction feed
 *
 * Fetches all non-pending transactions, filters out IGNORE client-side.
 * Shows BUSINESS_EXPENSE, ART_SUPPLY, ART_INCOME, PERSONAL, UNCERTAIN in feed.
 * Exports uncertainTransactions separately for review queue.
 */

import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/contexts/AuthContext';

export interface ArtTransaction {
  id: string;
  date: string;
  amount: number;
  merchantName: string;
  merchantLogoUrl?: string | null;
  tpCategory: string;
  tpReason: string | null;
  tpConfidence: number | null;
  tpSuggestedCategory?: string | null;
  tpConfirmedBy?: string | null;
  pending: boolean;
  tpLinkedArtworkId?: string | null;
  excludeFromPreFill?: boolean;
  isRecurring?: boolean;
  source?: string | null;
  note?: string | null;
  accountId?: string | null;
}

interface UseArtTransactionsReturn {
  transactions: ArtTransaction[];
  uncertainTransactions: ArtTransaction[];
  isLoading: boolean;
  uncertainCount: number;
}

export function useArtTransactions(): UseArtTransactionsReturn {
  const { user, profile } = useAuth();
  const [transactions, setTransactions] = useState<ArtTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      setTransactions([]);
      return;
    }

    const txnRef = collection(db, 'users', user.uid, 'transactions');
    const q = query(
      txnRef,
      where('pending', '==', false),
      orderBy('date', 'desc'),
      limit(30)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const txns: ArtTransaction[] = snap.docs
          .map((d) => ({
            id: d.id,
            date: d.data().date || '',
            amount: d.data().amount || 0,
            merchantName: d.data().merchantName || d.data().name || 'Unknown',
            merchantLogoUrl: d.data().merchantLogoUrl || null,
            tpCategory: d.data().tpCategory,
            tpReason: d.data().tpReason || null,
            tpConfidence: d.data().tpConfidence ?? null,
            tpSuggestedCategory: d.data().tpSuggestedCategory || null,
            tpConfirmedBy: d.data().tpConfirmedBy || null,
            pending: d.data().pending || false,
            tpLinkedArtworkId: d.data().tpLinkedArtworkId || null,
            excludeFromPreFill: d.data().excludeFromPreFill || false,
            isRecurring: d.data().isRecurring || false,
            source: d.data().source || 'plaid',
            note: d.data().note || null,
            accountId: d.data().accountId || null,
          }))
          .filter((t) => t.tpCategory !== 'IGNORE');
        setTransactions(txns);
        setIsLoading(false);
      },
      (err) => {
        console.error('[useArtTransactions] onSnapshot error:', err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const uncertainTransactions = useMemo(
    () => transactions.filter((t) => t.tpCategory === 'UNCERTAIN'),
    [transactions]
  );

  const uncertainCount = uncertainTransactions.length;

  return { transactions, uncertainTransactions, isLoading, uncertainCount };
}

export default useArtTransactions;
